/**
 * CountrySelectMenu
 *
 * Custom country `<select/>` renderer for react-phone-number-input, wired in
 * via its official `countrySelectComponent` extension point (see the
 * `countrySelectComponent` prop in PhoneInputWithCountry). This is how the
 * library supports custom option rendering — it does not offer a
 * `renderOption`/`formatOptionLabel` prop, but it does let you fully replace
 * the country `<select/>` while it keeps managing state (selected value,
 * available `options`, flag icon component, onChange contract, etc).
 *
 * The open menu is rendered via a portal into `document.body`, anchored to
 * the trigger button's position. This is required because some callers embed
 * this input inside an `overflow: hidden` card (e.g. the phone-verification
 * modal, which needs `overflow: hidden` to mask its own rounded corners) —
 * without a portal, that ancestor clips the dropdown once it grows past the
 * card's edge, silently hiding whichever options don't fit (e.g. the last
 * country in the list). Portaling to `<body/>` escapes any such ancestor.
 *
 * Each option row shows: flag · country name · dial code (right-aligned).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import { SUPPORTED_COUNTRIES_MAP } from '../../config/supportedCountries'

interface CountrySelectOption {
  value?: string
  label: string
}

interface IconComponentProps {
  country?: string
  label: string
  'aria-hidden'?: boolean
}

interface Props {
  name?: string
  value?: string
  options: CountrySelectOption[]
  onChange: (value: string | undefined) => void
  onFocus?: () => void
  onBlur?: () => void
  disabled?: boolean
  readOnly?: boolean
  iconComponent: React.ComponentType<IconComponentProps>
  'aria-label'?: string
}

interface MenuPosition {
  top: number
  left: number
}

export function CountrySelectMenu({
  value,
  options,
  onChange,
  onFocus,
  onBlur,
  disabled,
  readOnly,
  iconComponent: Icon,
  name: _name,
  ...rest
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((o) => o.value === value)

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) {
      setPosition({ top: rect.bottom + 8, left: rect.left })
    }
  }

  // Position the portaled menu against the trigger as soon as it opens
  // (before paint, so it doesn't flash at (0, 0)).
  useLayoutEffect(() => {
    if (isOpen) updatePosition()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }
    // `capture: true` so this also catches scroll on nested scroll containers
    // (e.g. the modal's own scrollable overlay), which don't bubble a plain
    // window-level, non-capturing "scroll" listener.
    function handleReposition() {
      updatePosition()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (disabled || readOnly) return
    setIsOpen((open) => !open)
  }

  const handleSelect = (optionValue: string | undefined) => {
    setIsOpen(false)
    onChange(optionValue)
  }

  return (
    <div className="PhoneInputCountry-menu-container" {...rest}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || readOnly}
        onClick={handleToggle}
        onFocus={onFocus}
        onBlur={onBlur}
        className="country-select-trigger"
      >
        {selectedOption && <Icon country={selectedOption.value} label={selectedOption.label} aria-hidden />}
        <ChevronDown className="country-select-trigger-arrow" />
      </button>

      {isOpen && position && createPortal(
        <div
          ref={menuRef}
          className="country-select-menu"
          role="listbox"
          style={{ position: 'fixed', top: position.top, left: position.left }}
        >
          {options.map((option) => {
            const dialCode = option.value ? SUPPORTED_COUNTRIES_MAP.get(option.value as any)?.dialCode : undefined
            const isSelected = option.value === value
            return (
              <button
                key={option.value || 'ZZ'}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option.value)}
                className={`country-select-option${isSelected ? ' country-select-option--selected' : ''}`}
              >
                <Icon country={option.value} label={option.label} aria-hidden />
                <span className="country-select-option-name">{option.label}</span>
                {dialCode && <span className="country-select-option-dialcode">{dialCode}</span>}
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </div>
  )
}
